import { graphql } from "gatsby";
import React, { useState } from "react";
import RichText from "@elements/RichText";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { getContentfulPreviewPropsFn } from "@utils";
import { IdTooltip } from "@src/components/elements/IdTooltip";

function Star() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="32"
      viewBox="0 0 36 32"
      fill="none"
    >
      <path
        d="M19.9677 3.70678L22.4004 0.000185962L25.365 6.63303L22.8939 10.4047L19.9677 3.70678Z"
        fill="#1F5E41"
      />
      <path
        d="M30.2137 10.481L32.6197 6.6801L25.2715 6.70934L22.8005 10.481L30.2137 10.481Z"
        fill="#2C8C5E"
      />
      <path
        d="M16.0714 3.70678L13.6386 0.000185962L10.6741 6.63303L13.1451 10.4047L16.0714 3.70678Z"
        fill="#1F5E41"
      />
      <path
        d="M5.7336 10.4043L3.32757 6.60344L10.6757 6.63268L13.1468 10.4043L5.7336 10.4043Z"
        fill="#2C8C5E"
      />
      <path
        d="M30.9401 14.0462L35.1446 15.4529L29.4875 20.0114L25.2103 18.5843L30.9401 14.0462Z"
        fill="#1F5E41"
      />
      <path
        d="M27.0996 25.752L31.3885 27.1088L29.4857 20.0113L25.2085 18.5841L27.0996 25.752Z"
        fill="#2C8C5E"
      />
      <path
        d="M5.05991 13.7859L0.855353 15.1926L6.51246 19.7512L10.7897 18.324L5.05991 13.7859Z"
        fill="#1F5E41"
      />
      <path
        d="M8.90041 25.4918L4.61152 26.8486L6.51434 19.751L10.7915 18.3239L8.90041 25.4918Z"
        fill="#2C8C5E"
      />
      <path
        d="M12.0891 27.5036L12.1164 31.9371L18.2101 27.9811L18.186 23.4722L12.0891 27.5036Z"
        fill="#1F5E41"
      />
      <path
        d="M24.4096 27.5019L24.3632 32L18.2112 27.9813L18.1872 23.4724L24.4096 27.5019Z"
        fill="#2C8C5E"
      />
    </svg>
  );
}

export function SectionTop5({ entry: rawEntry }) {
  const entry = useLiveUpdates(rawEntry);
  const getSectionTop5PreviewProps = getContentfulPreviewPropsFn(
    entry.contentful_id
  );
  const [currentRankingIndex, setCurrentRankingIndex] = useState(0);
  const currentYear = entry.years?.[currentRankingIndex];

  const winner = currentYear?.ranking?.at(0);
  const getWinnerPreviewProps = getContentfulPreviewPropsFn(
    winner?.contentful_id
  );
  const winnerScores = [
    { name: "REP", value: winner?.repScore },
    { name: "GLOBAL", value: winner?.globalScore },
    { name: "ESG", value: winner?.esgScore },
  ].filter((score) => score.value);
  const winnerDescription =
    winner?.company?.description?.description ?? winner?.company?.description;
  const winnerCompanyLogo = getImage(winner?.company?.logo?.big);

  const remainingRankings = currentYear?.ranking?.slice(1);
  const overline = entry.overline;
  const headline = entry.headline;
  const yearOptions = entry.years?.map((year, idx) => (
    <option key={`${year.displayName}-${idx}`} value={idx}>
      Year {year.year}
    </option>
  ));
  const additionalInformation = !entry.additionalInformation?.json
    ? entry.additionalInformation
    : { raw: JSON.stringify(entry.additionalInformation?.json) };
  const reportLink = useLiveUpdates(currentYear?.reportLink);

  const handleCurrentYearChange = (e) => {
    setCurrentRankingIndex(e.target.value);
  };

  return (
    <section
      id={entry.contentful_id}
      className="bg-new-green-darker py-12 md:pt-16 font-atkinson text-white"
    >
      <IdTooltip id={entry.contentful_id} />
      <div className="inner-small">
        <div className="text-center mb-9">
          {overline && (
            <p className="flex gap-2 items-center justify-center text-rt-s mb-4">
              <Star />
              <span {...getSectionTop5PreviewProps("overline")}>
                {overline}
              </span>
            </p>
          )}
          {headline && (
            <h3
              className="text-rt-l mt-4 mb-6 text-center"
              {...getSectionTop5PreviewProps("headline")}
            >
              {headline}
            </h3>
          )}
          {!yearOptions?.at(1) && (
            <div className="h-40px px-4 md:w-auto">
              {currentYear?.displayName}
            </div>
          )}

          {yearOptions?.at(1) && (
            <select
              onChange={handleCurrentYearChange}
              className="bg-overlay h-40px px-4 border border-overlay-grey rounded-md appearance-none cursor-pointer hover:bg-overlay-dark w-full md:w-auto pr-8 bg-arrowDownWhite bg-no-repeat bg-right-3 min-w-[230px]"
              {...getSectionTop5PreviewProps("years")}
            >
              {yearOptions}
            </select>
          )}
        </div>

        {currentYear && (
          <div className="grid grid-cols-12 gap-5 md:gap-6">
            {winner && (
              <div
                className="col-span-12 md:col-span-5 border border-overlay-green p-4 md:p-6 rounded-md flex flex-col md:gap-5"
                {...getWinnerPreviewProps("displayName")}
              >
                <div className="hidden md:flex gap-4 items-start">
                  <div className="bg-white p-4 rounded-lg w-80px md:w-155px aspect-square">
                    <GatsbyImage
                      alt={
                        winnerCompanyLogo?.title || winner.company?.displayName
                      }
                      image={winnerCompanyLogo}
                      {...getContentfulPreviewPropsFn(
                        winner.company.contentful_id
                      )("logo")}
                    />
                  </div>
                  <div>
                    <p className="text-accent-10 text-h1 mb-2">#1</p>
                    {winner.company?.displayName && (
                      <p
                        className="my-2 text-rt-l"
                        {...getContentfulPreviewPropsFn(
                          winner?.company?.contentful_id
                        )("displayName")}
                      >
                        {winner.company?.displayName}
                      </p>
                    )}
                    {winner.repScore && (
                      <p
                        className="py-1 px-2 inline-flex items-center gap-2 rounded-md shadow-default bg-accent-4 text-rt-xs"
                        {...getContentfulPreviewPropsFn(winner?.contentful_id)(
                          "repScore"
                        )}
                      >
                        <span className="bg-new-green-lightest block w-11px h-11px rounded-full" />
                        REP SCORE {winner.repScore.toFixed(1)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="md:hidden">
                  <div className="flex justify-between">
                    <p className="text-accent-10 text-h1 mb-2">#1</p>
                    <div className="bg-white p-4 rounded-lg w-80px md:w-155px aspect-square">
                      <GatsbyImage
                        alt={
                          winnerCompanyLogo?.title ||
                          winner.company?.displayName
                        }
                        image={winnerCompanyLogo}
                        {...getContentfulPreviewPropsFn(
                          winner.company.contentful_id
                        )("logo")}
                      />
                    </div>
                  </div>

                  <div>
                    {winner.company?.displayName && (
                      <p
                        className="text-rt-l"
                        {...getContentfulPreviewPropsFn(
                          winner?.company?.contentful_id
                        )("displayName")}
                      >
                        {winner.company?.displayName}
                      </p>
                    )}
                    <div className="mt-4 mb-5 flex gap-2">
                      {winnerScores.map((score, index) => (
                        <p
                          key={`${index}-${score.value}`}
                          className="py-1 px-2 inline-flex items-center gap-2 rounded-md shadow-default bg-accent-4 text-rt-xs"
                          {...getContentfulPreviewPropsFn(
                            winner?.contentful_id
                          )("repScore")}
                        >
                          <span className="bg-new-green-lightest block w-11px h-11px rounded-full" />
                          {score.name} {score.value}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                {reportLink?.url && reportLink?.label && (
                  <div
                    className="mt-auto"
                    {...getContentfulPreviewPropsFn(reportLink.contentful_id)(
                      "label"
                    )}
                  >
                    <a
                      href={reportLink?.url}
                      className="btn-new btn-new--accent-10"
                      rel="noreferrer noopener"
                      {...getContentfulPreviewPropsFn(
                        currentYear?.contentful_id
                      )("reportLink")}
                    >
                      <span
                        {...getContentfulPreviewPropsFn(
                          reportLink?.contentful_id
                        )("label")}
                      >
                        {reportLink.label}
                      </span>
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="col-span-12 md:col-span-7 pb-12 md:pb-0">
              <div className="flex flex-col gap-2">
                {remainingRankings?.map((ranking, idx) => (
                  <div
                    key={ranking.company?.displayName}
                    className="flex gap-5 items-center border-b border-accent-5-alpha md:border md:border-overlay-green md:px-5 py-4 md:rounded-md"
                    {...getContentfulPreviewPropsFn(ranking.contentful_id)(
                      "displayName"
                    )}
                  >
                    <p className="text-rt-2m md:text-rt-l text-accent-10 min-w-20px">
                      {idx + 2}
                    </p>
                    <div className="flex gap-4 items-center w-full">
                      <div className="aspect-square p-2 w-44px md:w-60px bg-white shrink-0 rounded-lg">
                        <GatsbyImage
                          alt={
                            ranking.company?.logo?.title ||
                            ranking.company?.displayName
                          }
                          image={getImage(ranking.company?.logo?.small)}
                          {...getContentfulPreviewPropsFn(
                            ranking.company.contentful_id
                          )("logo")}
                        />
                      </div>
                      <p
                        className="w-full text-18px md:text-rt-m"
                        {...getContentfulPreviewPropsFn(
                          ranking.company.contentful_id
                        )("displayName")}
                      >
                        {ranking.company?.displayName}
                      </p>
                      {ranking.repScore && (
                        <p
                          className="py-1 px-2 inline-flex items-center gap-2 rounded-md shadow-default bg-accent-4 text-rt-xs shrink-0"
                          {...getContentfulPreviewPropsFn(
                            ranking.contentful_id
                          )("repScore")}
                        >
                          <span className="md:inline bg-new-green-lightest w-11px h-11px rounded-full" />
                          <span>REP</span>
                          <span className="hidden md:inline">SCORE</span>
                          <span>{ranking.repScore.toFixed(1)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {additionalInformation?.raw && (
          <div
            className="my-6 bg-accent-7 py-5 px-4 rounded-lg hidden md:block"
            {...getSectionTop5PreviewProps("additionalInformation")}
          >
            <RichText bodyRichText={additionalInformation} />
          </div>
        )}
      </div>
    </section>
  );
}

export const SectionTop5Fragment = graphql`
  fragment SectionTop5 on ContentfulSectionTop5 {
    contentful_id
    __typename
    sys {
      id
    }
    overline
    headline
    years {
      ...SectionTop5Years
    }
    additionalInformation {
      ...SectionTop5AdditionalInformation
    }
  }
`;

export const SectionTop5AdditionalInformation = graphql`
  fragment SectionTop5AdditionalInformation on ContentfulSectionTop5AdditionalInformation {
    raw
  }
`;

export const SectionTop5Years = graphql`
  fragment SectionTop5Years on ContentfulRanking {
    contentful_id
    __typename
    sys {
      id
    }
    year
    reportLink {
      contentful_id
      __typename
      sys {
        id
      }
      url
      label
    }
    ranking {
      ...SectionTop5RepScore
    }
  }
`;

export const SectionTop5RepScore = graphql`
  fragment SectionTop5RepScore on ContentfulRepScore {
    contentful_id
    __typename
    sys {
      id
    }
    repScore
    company {
      ...SectionTop5Company
    }
  }
`;

export const SectionTop5Company = graphql`
  fragment SectionTop5Company on ContentfulCompany {
    contentful_id
    __typename
    sys {
      id
    }
    displayName
    description {
      description
    }
    logo {
      title
      big: gatsbyImageData(
        placeholder: BLURRED
        height: 123
        width: 123
        resizingBehavior: PAD
      )
      small: gatsbyImageData(
        placeholder: BLURRED
        height: 44
        width: 44
        resizingBehavior: PAD
      )
    }
  }
`;

export const SectionTop5CTA = graphql`
  fragment SectionTop5CTA on ContentfulCta {
    contentful_id
    __typename
    sys {
      id
    }
    url
    label
  }
`;
